import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { BlockForm, OrchardMap } from '../../components/orchard';
import { Layers, CheckCircle } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { Farm, GeoJSONPolygon } from '../../types/orchard';

function BlockCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const farmIdParam = searchParams.get('farm_id');
  const farmId = Number(farmIdParam);
  const orgIdParam = searchParams.get('org_id');
  const targetOrgId = orgIdParam ? parseInt(orgIdParam, 10) : undefined;

  const [farm, setFarm] = useState<Farm | null>(null);
  const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
  const [areaFromMap, setAreaFromMap] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);

  useEffect(() => {
    if (!farmId) return;
    orchardService.getFarm(farmId, targetOrgId).then((response) => {
      if (response.success && response.data) {
        setFarm(response.data);
      }
    }).catch(() => {
      setError('Could not load farm data');
    });
  }, [farmId, targetOrgId]);

  const handlePolygonCreated = useCallback(async (newPolygon: GeoJSONPolygon) => {
    setPolygon(newPolygon);
    try {
      const response = await orchardService.calculateArea(newPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handlePolygonEdited = useCallback(async (editedPolygon: GeoJSONPolygon) => {
    setPolygon(editedPolygon);
    try {
      const response = await orchardService.calculateArea(editedPolygon);
      if (response.success && response.data) {
        setAreaFromMap(response.data.hectares);
      }
    } catch {
      setAreaFromMap(null);
    }
  }, []);

  const handleSubmit = async (fieldData: {
    name: string;
    description?: string;
    area_hectares?: number;
    active?: boolean;
  }) => {
    if (!polygon || !farmId) return;

    setIsSubmitting(true);
    setError(null);

    const centerPoint = orchardService.calculateCenterPoint(polygon.coordinates);

    try {
      const response = await orchardService.createBlock({
        farm_id: farmId,
        name: fieldData.name,
        description: fieldData.description,
        boundary_geometry: polygon,
        center_point: centerPoint,
        area_hectares: fieldData.area_hectares ?? areaFromMap ?? undefined,
        active: fieldData.active,
        target_org_id: targetOrgId,
      });

      if (response.success && response.data) {
        setSuccessId(response.data.id);
        const qs = targetOrgId ? `?org_id=${targetOrgId}` : '';
        setTimeout(() => {
          navigate(`/orchard/blocks/${response.data!.id}${qs}`);
        }, 1500);
      } else {
        setError((response as any).message || 'Failed to create block');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!farmIdParam || !farmId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="New Block"
          subtitle="A farm must be selected"
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <p className="text-neutral-600 mb-4">No farm specified. Please navigate from a farm's detail page.</p>
        </div>
      </div>
    );
  }

  if (successId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="New Block"
          subtitle={farm ? `Block within ${farm.name}` : 'Block created'}
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Block Created!</h2>
              <p className="text-neutral-600">Redirecting to block details...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="New Block"
        subtitle={farm ? `Adding block to ${farm.name}` : 'Draw a block boundary on the map'}
        icon={<Layers className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-neutral-900 mb-4">Block Details</h2>
              <BlockForm
                areaFromMap={areaFromMap}
                hasPolygon={!!polygon}
                onSubmit={handleSubmit}
                onCancel={() => navigate(farmId ? `/orchard/farms/${farmId}` : '/orchard')}
                isSubmitting={isSubmitting}
              />
            </CardContent>
          </Card>

          {/* Map */}
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-800 font-medium">
                The green outline shows your farm boundary. Click the map to add boundary points, then click ✓ Finish (top-right) to complete the block.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '560px' }}>
              <OrchardMap
                farmBoundary={farm?.boundary_geometry}
                drawMode="block"
                onPolygonCreated={handlePolygonCreated}
                onPolygonEdited={handlePolygonEdited}
                center={
                  farm?.center_point
                    ? [farm.center_point.lat, farm.center_point.lng]
                    : undefined
                }
                zoom={15}
              />
            </div>
            {polygon && (
              <p className="text-sm text-success-700 font-medium">
                ✓ Block boundary drawn{areaFromMap ? ` · ${areaFromMap.toFixed(2)} ha` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlockCreate;
